import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService, FilterService, MessageService, SelectItem } from 'primeng/api';
import { DocumentEmailReadService } from 'src/app/application/service/DocumentEmailReadService';
import { DocumentEmailReads, DocumentEmailReadsParams } from 'src/app/domain/models/DocumentEmailRead';
import { PageResult } from 'src/app/domain/models/PageResult';
import { AuthGateway } from 'src/app/application/gateway/AuthGateway';
import { BasicUser } from 'src/app/domain/models/User';
import * as moment from 'moment';
import { DocumentServiceGateway } from 'src/app/application/gateway/DocumentServiceGateway';
import { ProfileUtils } from '../../util/profile-utils';
import { OperationDocumentType } from 'src/app/domain/models/Operation';
import { ImportOrderInformation } from 'src/app/domain/models/ImportOrder';

@Component({
  selector: 'app-downloaded-documents-table',
  templateUrl: './downloaded-documents-table.component.html',
  styleUrls: ['./downloaded-documents-table.component.css'],
  providers: [ConfirmationService]
})
export class DownloadedDocumentsTableComponent implements OnInit {

  @Input() modalMode: boolean = false;
  @Input() operationDocumentTypes: OperationDocumentType[] = [];

  @Output() selectionWithTypesChanged = new EventEmitter<Array<{documentId: number, documentTypeId: number}>>();

  // Map para almacenar el tipo de documento seleccionado por cada documento
  selectedDocumentTypes: Map<number, number> = new Map();

  documents: DocumentEmailReads[] = []
  filteredDocuments: DocumentEmailReads[] = []
  _selectedDocuments: DocumentEmailReads[] = []

  totalRecords: number = 0;
  pageSize: number = 10;
  currentPage: number = 1;
  searchTerm: string = '';

  subjectArray: string[] = []
  subjectFilter: SelectItem[] = []

  issuerNameArray: string[] = []
  issuerNameFilter: SelectItem[] = []

  issuerEmailArray: string[] = []
  issuerEmailFilter: SelectItem[] = []

  profileArray: string[] = []
  profileFilter: SelectItem[] = []

  statusArray: string[] = []
  statusFilter: SelectItem[] = []

  @Input()
  orderId!: string

  @Input()
  order: ImportOrderInformation | undefined

  @Output()
  OnAssignFiles = new EventEmitter();

  @Input()
  importOrderInformation!: ImportOrderInformation | undefined;


  isLoading: boolean = false;
  filterSuggested: boolean = true;

  user: BasicUser | undefined;

  get selectedDocuments(): DocumentEmailReads[] {
    return this._selectedDocuments;
  }

  set selectedDocuments(value: DocumentEmailReads[]) {
    this._selectedDocuments = value;
    this.selectionWithTypesChanged.emit(this.getSelectedDocumentsWithTypes());
  }

  constructor(
    private readonly documentEmailReadService: DocumentEmailReadService,
    private readonly _confirmationService: ConfirmationService,
    private readonly _messageService: MessageService,
    private readonly translate: TranslateService,
    private readonly filterService: FilterService,
    private readonly authService: AuthGateway,
    private readonly documentService: DocumentServiceGateway,
  ) {
  }

  ngOnInit(): void {
    this.user = this.authService.getUser();

    this.loadData();

    this.filterService.register('filter-date', (value: any, filter: any): boolean => {
      if (!filter) return true
      const found = moment(value).format("DD-MM-YYYY") === moment(filter).format("DD-MM-YYYY")
      return found;
    });
  }

  loadData(pageNumber: number = 1, pageSize: number = this.pageSize, searchTerm?: string) {
    this.isLoading = true;

    const params: DocumentEmailReadsParams = {
      pageNumber: pageNumber,
      pageSize: pageSize,
      orderId: this.orderId
    };

    // Agregar término de búsqueda si existe
    if (searchTerm?.trim()) {
      params.text = searchTerm.trim();
    }

    // Endpoint que retorna todo el listado de documentos
    let request = this.documentEmailReadService.getDocumentEmailReads(params);
    if (this.modalMode) {
      // Endpoint que retorna el listado de documentos disponibles para asignar
      if(this.filterSuggested && this.modalMode) {
        params.container = this.order?.delivery?.containerNumber
        params.booking = this.order?.delivery?.booking
        params.ship = this.order?.delivery?.ship
      }

      request = this.documentEmailReadService.getDocumentToAssign(params);
    }

    request.subscribe({
      next: (response: PageResult<DocumentEmailReads>) => {
        this.documents = response.items;
        this.filteredDocuments = response.items;

        this.totalRecords = response.totalCount;
        this.currentPage = response.pageNumber;
        this.pageSize = response.pageSize;

        this.filteredDocuments.forEach(x => {
          this.setFilters(x);
        });
        this.isLoading = false;
      },
      error: (e) => {
        this.isLoading = false;
        this._messageService.add({
          severity: 'error',
          summary: this.translate.instant('MessageService.Summary.FailedAction'),
          detail: this.translate.instant('MessageService.Summary.FailedActionMessage')
        });
      }
    });
  }

  onPageChange(event: any) {
    this.currentPage = event.page + 1;
    this.pageSize = event.rows;
    this.loadData(this.currentPage, this.pageSize, this.searchTerm);
  }

  onPageSizeChange(event: any) {
    this.pageSize = event.value;
    this.currentPage = 1;
    this.loadData(this.currentPage, this.pageSize, this.searchTerm);
  }

  onSearch() {
    this.currentPage = 1;
    this.loadData(this.currentPage, this.pageSize, this.searchTerm);
  }

  getLastRecordNumber(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalRecords);
  }

  onFilter() {
    if (this.searchTerm?.trim()) {
      this.onSearch();
    } else {
      // Si se limpia la búsqueda, recargar datos sin filtro
      this.currentPage = 1;
      this.loadData(this.currentPage, this.pageSize);
    }
  }

  sortFiters() {
    this.subjectFilter.sort((a, b) => a.value - b.value)
    this.issuerNameFilter.sort((a, b) => a.value - b.value)
    this.issuerEmailFilter.sort((a, b) => a.value.localeCompare(b.value))
    this.profileFilter.sort((a, b) => a.value.localeCompare(b.value))
    this.statusFilter.sort((a, b) => a.value.localeCompare(b.value))
  }

  applyFilters(item: DocumentEmailReads): boolean {
    if (this.searchTerm.trim()) {
      const isUrl = item.url?.toString() ? item.url.toString().toLocaleLowerCase().includes(this.searchTerm.toLowerCase()): false;
      const isSubject = item.subject?.toString() ? item.subject.toString().toLocaleLowerCase().includes(this.searchTerm.toLowerCase()): false;
      const isIssuerName = item.fromName ? item.fromName.toString().toLocaleLowerCase().includes(this.searchTerm.toLowerCase()): false;
      const isIssuerEmail = item.email ? item.email.toString().toLocaleLowerCase().includes(this.searchTerm.toLowerCase()): false;
      const isProfile = item.profileId ? item.profileId.toString().toLocaleLowerCase().includes(this.searchTerm.toLowerCase()) : false;
      const isStatus = this.getStatusLabel(item.assigned).toLocaleLowerCase().includes(this.searchTerm.toLowerCase());
      const isMatchDate = item.matchDate ? moment(item.matchDate).format("DD-MM-YYYY").toString().toLocaleLowerCase().includes(this.searchTerm.toLowerCase()): false;

      return isUrl || isSubject || isIssuerName || isIssuerEmail || isProfile || isStatus || isMatchDate
    }

    return true;
  }

  clearSelectFilters() {
    this.subjectArray = [];
    this.subjectFilter = [];

    this.issuerNameArray = [];
    this.issuerNameFilter = [];

    this.issuerEmailArray = [];
    this.issuerEmailFilter = [];

    this.profileArray = [];
    this.profileFilter = [];

    this.statusArray = [];
    this.statusFilter = [];
  }

  setFilters(row: DocumentEmailReads) {
    // Options para filtro por asunto
    if (row.subject && !this.subjectArray.includes(row.subject)) {
      this.subjectArray.push(row.subject)
      this.subjectFilter.push({value: row.subject, label: row.subject.toString()})
    }

    // Options para filtro por nombre emisor
    if (row.fromName && !this.issuerNameArray.includes(row.fromName)) {
      this.issuerNameArray.push(row.fromName)
      this.issuerNameFilter.push({value: row.fromName, label: row.fromName.toString()})
    }

    // Options para filtro por correo emisor
    if (row.email && !this.issuerEmailArray.includes(row.email)) {
      this.issuerEmailArray.push(row.email)
      this.issuerEmailFilter.push({value: row.email, label: row.email.toString()})
    }

    // Options para filtro por perfil
    if (row.profileName && !this.profileArray.includes(row.profileName)) {
      this.profileArray.push(row.profileName)
      this.profileFilter.push({value: row.profileName, label: row.profileName.toString()})
    }

    // Options para filtro por estado
    const statusLabel = this.getStatusLabel(row.assigned);
    if (!this.statusArray.includes(statusLabel)) {
      this.statusArray.push(statusLabel);
      this.statusFilter.push({value: row.assigned, label: statusLabel});
    }
  }

  getStatusLabel(assigned: boolean): string {
    if (!this.user) return assigned ? 'Assigned' : 'Unassigned';

    switch (this.user.lang) {
      case 'es':
        return assigned ? 'Asignado' : 'Sin asignar';
      case 'en':
      default:
        return assigned ? 'Assigned' : 'Unassigned';
    }
  }

  onDelete(documentEmailReadId: string) {
    if (documentEmailReadId) {
      this._confirmationService.confirm({
        header: this.translate.instant('AdminDownloadedDocuments.Messages.ConfirmDeleteHeader'),
        message: this.translate.instant('AdminDownloadedDocuments.Messages.ConfirmDeleteMessage'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.isLoading = true;

          this.documentEmailReadService.deleteDocument(documentEmailReadId).subscribe({
            next: () => {
              this._messageService.add({
                severity: 'success',
                summary: 'Acción exitosa',
                detail: 'El archivo ha sido eliminado satisfactoriamente.'
              });
              this.isLoading = false;
            },
            error: (e) => {
              this.isLoading = false;
              this._messageService.add({
                severity: 'error',
                summary: 'Acción fallida',
                detail: e?.description ?? 'Ha ocurrido un error eliminando el archivo. Por favor, contacte con soporte.'
              });
            }
          });
        }
      })
    }
    else {
      this._messageService.add({
        severity: 'error',
        summary: 'Acción fallida',
        detail: 'Ha ocurrido un error eliminando el archivo. Por favor, contacte con soporte.'
      });
    }
  }

  onDownloadDocument(guid: string) {
    if (guid) {
      // if (this.modalMode) {
        // Descarga de archivos provenientes de email
        this.documentEmailReadService.getEmailDocument(guid).subscribe((resultBlob: any) => {
          let downloadURL = window.URL.createObjectURL(resultBlob.body);
          window.open(downloadURL);
        });
      // }
      // else {
      //   // Descarga de archivos cargados
      //   this.documentService.getOperationDocument(guid).subscribe((resultBlob: any) => {
      //     let downloadURL = window.URL.createObjectURL(resultBlob.body);
      //     window.open(downloadURL);
      //   });
      // }
    }
    else {
      this._messageService.add({
        severity: 'error',
        summary: this.translate.instant('AdminDownloadedDocuments.Messages.FailedActionHeader'),
        detail: this.translate.instant('AdminDownloadedDocuments.Messages.FailedGetDocumentMessage')
      });
    }
  }

  getFileName(url?: string): string {
    return ProfileUtils.getFileName(url);
  }

  getSelectedDocumentType(documentId: number): number | null {
    return this.selectedDocumentTypes.get(documentId) || null;
  }

  setSelectedDocumentType(documentId: number, documentTypeId: number) {
    this.selectedDocumentTypes.set(documentId, documentTypeId);
    this.selectionWithTypesChanged.emit(this.getSelectedDocumentsWithTypes());
  }

  validateSelectedDocuments(): boolean {
    return this.selectedDocuments.every(doc =>
      this.selectedDocumentTypes.has(doc.id) && this.selectedDocumentTypes.get(doc.id) !== null
    );
  }

  getSelectedDocumentsWithTypes(): Array<{documentId: number, documentTypeId: number}> {
    return this.selectedDocuments.map(doc => ({
      documentId: doc.id,
      documentTypeId: this.selectedDocumentTypes.get(doc.id)!
    }));
  }
}

